/**
 * №29 (S2) — THE RECORD SUBJECT'S RENDERER REGISTRY.
 *
 * Some entity types have a specialised record surface: a matter gets the №13 Matter
 * Cockpit, an invoice gets the №13.1 invoice detail, a fee arrangement gets the
 * arrangement detail. Everything else gets the generic four-plane record (№11 D-W1).
 *
 * ## Why this is a registry and not three routes
 *
 * Until S2 those three were REGISTERED ROUTES with the entity type baked into the
 * path — `place/matter/:id`, `place/invoice/:id`, `place/fee-arrangement/:id` —
 * sitting beside the generic `place/:entity/:id`. That worked only because a static
 * segment outranks a dynamic one in React Router's ranking, and it cost something
 * real: those patterns score `1 + 10 + 10 + 10 + 3 = 34`, which is an exact TIE with
 * `/app/place/:entity/chat` (`1 + 10 + 10 + 3 + 10`). A tie is broken by registration
 * order, so a Place-list conversation route would have rendered the matter cockpit
 * looking for a matter called "chat" on `/app/place/matter/chat`, while working
 * correctly on `/app/place/contact/chat`. S1 declined to ship a control that is right
 * on some Places and silently wrong on others, and recorded the reason (§S1.6).
 *
 * Retiring them removes the tie. **Which renderer draws a record is not a routing
 * question** — it is one route, `/app/place/:entity/:id`, and the entity type picks
 * the component. That is also what the route→subject matrix already said: all three
 * resolved to the same `place-record` row, because a specialised renderer was never
 * supposed to mean a different anchor.
 *
 * Each specialised page reads `useParams<{ id }>()`, and `:id` is still `:id` on the
 * generic pattern, so nothing about the pages themselves changes.
 */
import type { ComponentType } from "react";
import { MatterCockpitPage } from "./MatterCockpitPage";
import { ArrangementDetailPage } from "./ArrangementDetailPage";
import { InvoiceDetailPage } from "./InvoicesPage";

/** entity type slug → the surface that draws THAT type's record. */
export const RECORD_RENDERERS: Record<string, ComponentType> = {
  matter: MatterCockpitPage,
  invoice: InvoiceDetailPage,
  "fee-arrangement": ArrangementDetailPage,
};

export function recordRendererFor(entity: string | undefined): ComponentType | undefined {
  return entity ? RECORD_RENDERERS[entity] : undefined;
}
